import { Link } from "react-router-dom";
import { ArrowLeft, RotateCcw, ExternalLink, Activity, BarChart3 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@fox1ck-org/ui/toast";
import type { AppDetailResponse } from "@/api/client";
import { api } from "@/api/client";
import { Button, cn, Separator } from "@fox1ck-org/ui";
import { SyncButton } from "@/components/SyncButton";
import { healthLabel } from "@/components/shared/status-badge";
import { formatRelative } from "./time";

interface AppStatusHeaderProps {
  app: AppDetailResponse;
}

const HEALTH_DOT_CLASSES: Record<string, string> = {
  Healthy: "bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.5)]",
  Progressing: "bg-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.5)] animate-pulse",
  Degraded: "bg-red-500 shadow-[0_0_12px_rgba(239,68,68,0.5)]",
  Suspended: "bg-zinc-500",
  Missing: "bg-yellow-500 shadow-[0_0_12px_rgba(234,179,8,0.4)]",
  Unknown: "bg-zinc-500",
};

const SYNC_STYLES: Record<string, string> = {
  Synced: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  OutOfSync: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  Unknown: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

/**
 * Sticky-feeling status header for AppDetail. The big colored dot is the
 * primary health affordance — operators see "is this app OK" before they
 * scroll a single pixel.
 */
export function AppStatusHeader({ app }: AppStatusHeaderProps) {
  const queryClient = useQueryClient();
  const restartMutation = useMutation({
    mutationFn: () => api.cluster.restart(app.name),
    onSuccess: () => {
      toast.success("Перезапуск запущен");
      queryClient.invalidateQueries({ queryKey: ["cluster-app-status", app.name] });
    },
    onError: (err) => toast.error(`Перезапуск не удался: ${(err as Error).message}`),
  });

  const healthStatus = app.health.status;
  const syncStatus = app.sync.status;
  const dotClass = HEALTH_DOT_CLASSES[healthStatus] ?? HEALTH_DOT_CLASSES.Unknown!;
  const syncClass = SYNC_STYLES[syncStatus] ?? SYNC_STYLES.Unknown!;
  const isProgressing = app.operationState?.phase === "Running";

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <Button variant="ghost" size="icon" asChild className="mt-0.5 shrink-0">
            <Link to="/" aria-label="К обзору">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>

          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className={cn(
                  "h-3 w-3 rounded-full shrink-0",
                  dotClass
                )}
                aria-label={`Состояние: ${healthLabel(healthStatus)}`}
              />
              <h1 className="text-[17px] font-semibold text-foreground tracking-tight truncate">
                {app.name}
              </h1>
              <span
                className={cn(
                  "px-2 py-0.5 rounded text-[11px] font-semibold uppercase border tracking-wide",
                  syncClass
                )}
              >
                {syncStatus}
              </span>
            </div>

            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
              <span className="text-xs">{healthLabel(healthStatus)}</span>
              <Separator orientation="vertical" className="h-3" />
              <span>{app.namespace}</span>
              <Separator orientation="vertical" className="h-3" />
              <span>проект · {app.project}</span>
              {app.sync.revision && (
                <>
                  <Separator orientation="vertical" className="h-3" />
                  <span className="font-mono">{app.sync.revision.slice(0, 7)}</span>
                </>
              )}
              {app.reconciledAt && (
                <>
                  <Separator orientation="vertical" className="h-3" />
                  <span>сверено {formatRelative(app.reconciledAt)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <SyncButton appName={app.name} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => restartMutation.mutate()}
            disabled={restartMutation.isPending}
          >
            <RotateCcw
              className={cn("h-4 w-4 mr-1", restartMutation.isPending && "animate-spin")}
            />
            Перезапустить
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <a href={app.argocdUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-1" />
              ArgoCD
            </a>
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <a href={app.grafanaUrl} target="_blank" rel="noopener noreferrer">
              <BarChart3 className="h-4 w-4 mr-1" />
              Grafana
            </a>
          </Button>
        </div>
      </div>

      {/* Operation state banner */}
      {app.operationState && (
        <div
          className={cn(
            "rounded-lg border px-3 py-2 text-sm flex items-center gap-3",
            app.operationState.phase === "Succeeded"
              ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-400"
              : isProgressing
                ? "border-blue-500/30 bg-blue-500/5 text-blue-400"
                : app.operationState.phase === "Failed" || app.operationState.phase === "Error"
                  ? "border-red-500/30 bg-red-500/5 text-red-400"
                  : "border-yellow-500/30 bg-yellow-500/5 text-yellow-400"
          )}
        >
          <Activity className={cn("h-4 w-4 shrink-0", isProgressing && "animate-pulse")} />
          <div className="flex-1 min-w-0">
            <div className="font-medium">{app.operationState.phase}</div>
            {app.operationState.message && (
              <div className="text-xs opacity-80 truncate">{app.operationState.message}</div>
            )}
          </div>
          {app.operationState.finishedAt && (
            <span className="text-xs opacity-60 whitespace-nowrap">
              {formatRelative(app.operationState.finishedAt)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
